/**
 * Config-version reconciliation.
 *
 * The backend bumps a counter whenever a tenant's module entitlements or
 * guardian configuration change (`tenants.config_version`), or whenever
 * fleet-wide module availability changes (`platform_config_version`), and
 * echoes both back on **every** API response as
 * `X-Config-Version: <tenant>.<platform>`.
 *
 * This module compares that header against what Redux holds and triggers one
 * debounced `/auth/current-user` refetch when it is higher.
 *
 * ## Why this exists rather than relying on the WebSocket event
 *
 * The `tenant_config_updated` push gives sub-second latency in the normal case,
 * but it cannot be the only signal. This repo already learned that lesson the
 * hard way with boolean-string generation. Concretely:
 *
 * - `REDIS_URL` may be unset, or Redis may have a bad minute — and the stack
 *   runs **two** FastAPI containers behind nginx, so an event published by one
 *   never reaches sockets held by the other.
 * - `_pending_events` replay is a per-worker in-memory deque capped at 50 per
 *   user; it only helps on reconnect to the *same* worker.
 * - A sleeping laptop misses everything.
 *
 * The header piggybacks on requests that were happening anyway: no polling
 * loop, no timer, no wasted traffic, and self-healing by construction. The very
 * next request the user makes corrects the UI.
 *
 * Presentation only, either way. The backend re-reads module state per request,
 * so there is never a window in which a disabled module is actually *usable* —
 * the lag is cosmetic.
 *
 * The refresh callback is registered by the app root rather than imported here,
 * because `lib/api.js` importing the Redux store would close an import cycle
 * (store → reducers → services → lib/api).
 */

export const CONFIG_VERSION_HEADER = "x-config-version";

// Coalesce bursts: a page load fires many requests at once, all carrying the
// same new version, and a SuperAdmin saving twelve modules in one go produces
// one bump the client should answer once.
const REFRESH_DEBOUNCE_MS = 400;

let refreshCallback = null;
let debounceTimer = null;
let pending = false;

/**
 * Register the "our config is stale, refetch it" callback. Called once from the
 * app root with a `dispatch(currentUser())`.
 *
 * @returns {Function} unregister
 */
export const registerConfigRefresh = (fn) => {
  refreshCallback = typeof fn === "function" ? fn : null;
  return () => {
    if (refreshCallback === fn) refreshCallback = null;
  };
};

const scheduleRefresh = () => {
  if (!refreshCallback) return;
  pending = true;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    if (!pending || !refreshCallback) return;
    pending = false;
    try {
      refreshCallback();
    } catch (err) {
      // A failed refetch is not fatal: the header comes back on the next
      // response and we try again.
      console.error("[Config] Refresh failed:", err);
    }
  }, REFRESH_DEBOUNCE_MS);
};

/** Force a refetch regardless of versions — used on WS event and reconnect. */
export const requestConfigRefresh = () => scheduleRefresh();

const parseHeader = (value) => {
  if (!value || typeof value !== "string") return null;
  const [tenant, platform] = value.split(".");
  const tenantVersion = Number.parseInt(tenant, 10);
  const platformVersion = Number.parseInt(platform, 10);
  if (Number.isNaN(tenantVersion) && Number.isNaN(platformVersion)) return null;
  return {
    tenantVersion: Number.isNaN(tenantVersion) ? 0 : tenantVersion,
    platformVersion: Number.isNaN(platformVersion) ? 0 : platformVersion,
  };
};

/**
 * Compare a response's `X-Config-Version` against the versions we hold and
 * schedule a refetch if either has moved on.
 *
 * `>` and not `!==`: a lower value means a stale read from another worker
 * mid-write, and refetching on that would ping-pong.
 *
 * @param {object} response         axios response
 * @param {object} knownVersions    `{ configVersion, platformConfigVersion }`
 */
export const checkConfigVersion = (response, knownVersions) => {
  const parsed = parseHeader(response?.headers?.[CONFIG_VERSION_HEADER]);
  if (!parsed) return;

  const knownTenant = Number(knownVersions?.configVersion ?? 0);
  const knownPlatform = Number(knownVersions?.platformConfigVersion ?? 0);

  if (parsed.tenantVersion > knownTenant || parsed.platformVersion > knownPlatform) {
    scheduleRefresh();
  }
};

/**
 * Where the interceptor reads the versions it compares against.
 *
 * Registered by the app root alongside the refresh callback, for the same
 * import-cycle reason. Returns null until registration, which makes the
 * interceptor a no-op rather than an error during early boot.
 */
let versionsGetter = null;

export const registerConfigVersionsGetter = (fn) => {
  versionsGetter = typeof fn === "function" ? fn : null;
  return () => {
    if (versionsGetter === fn) versionsGetter = null;
  };
};

/** Called from the axios success interceptor for every response. */
export const noteResponseConfigVersion = (response) => {
  if (!versionsGetter) return;
  try {
    checkConfigVersion(response, versionsGetter());
  } catch {
    // Never let bookkeeping break a successful response.
  }
};
